/**
 * Color palettes and themes for the mind map application
 */

// Main color palette
export const COLORS = [
  // Blues & Cyans
  { name: "Skyglint", gradient: "linear-gradient(135deg, #0056b3 0%, #003d82 100%)" },
  { name: "Seraphim Blue", gradient: "linear-gradient(135deg, #0080cc 0%, #2b5ce6 100%)" },
  { name: "Frostwink", gradient: "linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)" },
  { name: "Oceancloud", gradient: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)" },
  { name: "Deep Sea", gradient: "linear-gradient(135deg, #1a237e 0%, #283593 100%)" },

  // Purples & Violets
  { name: "Dreamburst", gradient: "linear-gradient(135deg, #4527a0 0%, #512da8 100%)" },
  { name: "Nightfire", gradient: "linear-gradient(135deg, #4a148c 0%, #6a1b9a 100%)" },
  { name: "Electric Haze", gradient: "linear-gradient(135deg, #ad1457 0%, #4a148c 100%)" },
  { name: "Lavashock", gradient: "linear-gradient(135deg, #8e24aa 0%, #5e35b1 100%)" },
  { name: "Mystdust", gradient: "linear-gradient(135deg, #7b1fa2 0%, #3f51b5 100%)" },

  // Pinks & Corals
  { name: "Coralite", gradient: "linear-gradient(135deg, #c2185b 0%, #e91e63 100%)" },
  { name: "Cherrywave", gradient: "linear-gradient(135deg, #d84315 0%, #c2185b 100%)" },
  { name: "Crimson Sky", gradient: "linear-gradient(135deg, #d32f2f 0%, #f57c00 100%)" },
  { name: "Rosegold", gradient: "linear-gradient(135deg, #ad1457 0%, #e53935 100%)" },
  { name: "Blushfire", gradient: "linear-gradient(135deg, #c2185b 0%, #e91e63 100%)" },

  // Oranges & Yellows
  { name: "Sunblush", gradient: "linear-gradient(135deg, #ef6c00 0%, #d84315 100%)" },
  { name: "Peachfuzz", gradient: "linear-gradient(135deg, #ff8f00 0%, #f57c00 100%)" },
  { name: "Amberfade", gradient: "linear-gradient(135deg, #ff8f00 0%, #e65100 100%)" },
  { name: "Goldflare", gradient: "linear-gradient(135deg, #ff6f00 0%, #ff8f00 100%)" },
  { name: "Honeywave", gradient: "linear-gradient(135deg, #e65100 0%, #ff6f00 100%)" },

  // Greens & Teals
  { name: "Lushbeam", gradient: "linear-gradient(135deg, #2e7d32 0%, #00695c 100%)" },
  { name: "Mossglow", gradient: "linear-gradient(135deg, #388e3c 0%, #1976d2 100%)" },
  { name: "Meadowmint", gradient: "linear-gradient(135deg, #00695c 0%, #4527a0 100%)" },
  { name: "Forestmist", gradient: "linear-gradient(135deg, #1b5e20 0%, #00695c 100%)" },
  { name: "Stormbliss", gradient: "linear-gradient(135deg, #424242 0%, #212121 100%)" }
];

// Color themes for different contexts
export const COLOR_THEMES = {
  default: {
    name: "Default",
    colors: COLORS
  },

  professional: {
    name: "Professional",
    colors: [
      { name: "Corporate Blue", gradient: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)" },
      { name: "Business Gray", gradient: "linear-gradient(135deg, #424242 0%, #212121 100%)" },
      { name: "Executive Green", gradient: "linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)" },
      { name: "Finance Orange", gradient: "linear-gradient(135deg, #ef6c00 0%, #d84315 100%)" },
      { name: "Success Teal", gradient: "linear-gradient(135deg, #00695c 0%, #004d40 100%)" }
    ]
  },

  creative: {
    name: "Creative",
    colors: [
      { name: "Artist Purple", gradient: "linear-gradient(135deg, #8e24aa 0%, #4a148c 100%)" },
      { name: "Designer Pink", gradient: "linear-gradient(135deg, #e91e63 0%, #ad1457 100%)" },
      { name: "Creative Orange", gradient: "linear-gradient(135deg, #ff8f00 0%, #e65100 100%)" },
      { name: "Inspiration Blue", gradient: "linear-gradient(135deg, #1e88e5 0%, #0d47a1 100%)" },
      { name: "Innovation Green", gradient: "linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)" }
    ]
  },

  pastel: {
    name: "Pastel",
    colors: [
      { name: "Soft Blue", gradient: "linear-gradient(135deg, #bbdefb 0%, #90caf9 100%)" },
      { name: "Gentle Pink", gradient: "linear-gradient(135deg, #f8bbd9 0%, #f48fb1 100%)" },
      { name: "Light Green", gradient: "linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)" },
      { name: "Pale Yellow", gradient: "linear-gradient(135deg, #fff9c4 0%, #fff59d 100%)" },
      { name: "Soft Purple", gradient: "linear-gradient(135deg, #e1bee7 0%, #ce93d8 100%)" }
    ]
  },

  dark: {
    name: "Dark Mode",
    colors: [
      { name: "Dark Blue", gradient: "linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)" },
      { name: "Deep Purple", gradient: "linear-gradient(135deg, #4a148c 0%, #6a1b9a 100%)" },
      { name: "Dark Green", gradient: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)" },
      { name: "Dark Orange", gradient: "linear-gradient(135deg, #d84315 0%, #ef6c00 100%)" },
      { name: "Charcoal", gradient: "linear-gradient(135deg, #212121 0%, #424242 100%)" }
    ]
  }
};

// Utility functions for color manipulation
export const ColorUtils = {
  /**
   * Get random color from current theme
   * @param {string} themeName - Theme name (default: 'default')
   * @returns {Object} Color object with name and gradient
   */
  getRandomColor(themeName = 'default') {
    const theme = COLOR_THEMES[themeName] || COLOR_THEMES.default;
    const randomIndex = Math.floor(Math.random() * theme.colors.length);
    return theme.colors[randomIndex];
  },

  /**
   * Get color by name from any theme
   * @param {string} colorName - Name of the color
   * @returns {Object|null} Color object or null if not found
   */
  getColorByName(colorName) {
    for (const theme of Object.values(COLOR_THEMES)) {
      const color = theme.colors.find(c => c.name === colorName);
      if (color) return color;
    }
    return null;
  },

  /**
   * Get contrasting text color for a background
   * @param {string} backgroundGradient - CSS gradient string
   * @returns {string} Either 'white' or 'black'
   */
  getContrastingTextColor(backgroundGradient) {
    // Extract first color from gradient for luminance calculation
    const colorMatch = backgroundGradient.match(/#[0-9a-fA-F]{6}/);
    if (!colorMatch) return 'white'; // Default to white if can't parse

    const hex = colorMatch[0];
    const luminance = this.getLuminance(hex);

    // Use white text for dark backgrounds, black for light
    return luminance > 0.5 ? 'black' : 'white';
  },

  /**
   * Calculate luminance of a hex color
   * @param {string} hex - Hex color string (e.g., '#ff0000')
   * @returns {number} Luminance value (0-1)
   */
  getLuminance(hex) {
    const r = parseInt(hex.substr(1, 2), 16) / 255;
    const g = parseInt(hex.substr(3, 2), 16) / 255;
    const b = parseInt(hex.substr(5, 2), 16) / 255;

    const [rs, gs, bs] = [r, g, b].map(c => {
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  },

  /**
   * Generate a gradient variant of an existing color
   * @param {string} baseColor - Base hex color
   * @param {number} lightnessAdjustment - Adjustment factor (-0.3 to 0.3)
   * @returns {string} CSS gradient string
   */
  generateGradientVariant(baseColor, lightnessAdjustment = 0.2) {
    // Simple implementation - in production, use a proper color library
    const darker = this.adjustBrightness(baseColor, -Math.abs(lightnessAdjustment));
    //const lighter = this.adjustBrightness(baseColor, Math.abs(lightnessAdjustment));

    return `linear-gradient(135deg, ${baseColor} 0%, ${darker} 100%)`;
  },

  /**
   * Adjust brightness of a hex color
   * @param {string} hex - Hex color string
   * @param {number} factor - Adjustment factor (-1 to 1)
   * @returns {string} Adjusted hex color
   */
  adjustBrightness(hex, factor) {
    const r = Math.max(0, Math.min(255, parseInt(hex.substr(1, 2), 16) + factor * 255));
    const g = Math.max(0, Math.min(255, parseInt(hex.substr(3, 2), 16) + factor * 255));
    const b = Math.max(0, Math.min(255, parseInt(hex.substr(5, 2), 16) + factor * 255));

    return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
  }
};

// Export default color set for backward compatibility
export { COLORS as default };